import { useEffect, useState } from 'react';
import { View, ScrollView, Platform, useWindowDimensions, Alert } from 'react-native';
import { Appbar, Card, Text, Button, Chip, useTheme, FAB, IconButton, Portal, Modal, Surface } from 'react-native-paper';
import { Sidebar, SIDEBAR_WIDTH } from '../../../src/components/Sidebar';
import Logo from '../../../src/components/Logo';
import { VehicleModal } from '../../../src/components/VehicleModal';
import { useRouter } from 'expo-router';
import { getMyVehicles, addMyVehicle, updateMyVehicle, unassignMyVehicle } from '../../../src/api/apiService';
import { useTranslation } from 'react-i18next';
import { useForm } from 'react-hook-form';
import { VehicleFormData } from '../../../src/lib/types';
import AsyncStorage from '@react-native-async-storage/async-storage';
import { useSafeAreaInsets } from 'react-native-safe-area-context';

interface Vehicle {
  id: number;
  registration_number: string;
  fuel_type?: string;
  make?: string;
  model?: string;
  year?: number;
}

const TOOLTIP_DISMISSED_KEY = '@my_vehicles_tooltip_dismissed';

export default function MyVehiclesScreen() {
  const theme = useTheme();
  const router = useRouter();
  const { t } = useTranslation();
  const { width, height } = useWindowDimensions();
  const insets = useSafeAreaInsets();
  const isLargeScreen = width > 1024;
  
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [loading, setLoading] = useState(true);
  const [vehicles, setVehicles] = useState<Vehicle[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [modalVisible, setModalVisible] = useState(false);
  const [editingVehicle, setEditingVehicle] = useState<Vehicle | null>(null);
  const [localRegNumber, setLocalRegNumber] = useState('');
  const [tooltipDismissed, setTooltipDismissed] = useState(true);

  const form = useForm<VehicleFormData>({
    defaultValues: {
      registration_number: '',
      fuel_type: 'gasolina',
      make: '',
      model: '',
      year: new Date().getFullYear(),
    },
  });

  useEffect(() => {
    loadVehicles();
    checkTooltip();
  }, []);

  const checkTooltip = async () => {
    try {
      const dismissed = await AsyncStorage.getItem(TOOLTIP_DISMISSED_KEY);
      setTooltipDismissed(dismissed === 'true');
    } catch (e) {
      console.error('Error checking tooltip state:', e);
    }
  };

  const dismissTooltip = async () => {
    try {
      await AsyncStorage.setItem(TOOLTIP_DISMISSED_KEY, 'true');
      setTooltipDismissed(true);
    } catch (e) {
      console.error('Error saving tooltip state:', e);
    }
  };

  const loadVehicles = async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await getMyVehicles();
      if (response.ok) {
        setVehicles(response.data.vehicles || []);
      } else {
        setError('Failed to load vehicles');
      }
    } catch (e: any) {
      setError(e?.message || 'Error loading vehicles');
    } finally {
      setLoading(false);
    }
  };

  const handleAddVehicle = async (data: VehicleFormData) => {
    try {
      const response = await addMyVehicle(
        data.registration_number,
        data.fuel_type,
        data.make,
        data.model,
        data.year
      );
      
      if (response.ok) {
        setModalVisible(false);
        form.reset();
        setLocalRegNumber('');
        loadVehicles();
        return true;
      } else {
        const errorData = response.data as any;
        setError(errorData?.detail || 'Failed to add vehicle');
        return false;
      }
    } catch (e: any) {
      setError(e?.message || 'Error adding vehicle');
      return false;
    }
  };

  const handleEditVehicle = async (data: VehicleFormData) => {
    if (!editingVehicle) return false;
    
    try {
      // Only send editable fields (not registration_number)
      const response = await updateMyVehicle(editingVehicle.id, {
        fuel_type: data.fuel_type,
        make: data.make,
        model: data.model,
        year: data.year,
      });
      
      if (response.ok) {
        setModalVisible(false);
        setEditingVehicle(null);
        form.reset();
        setLocalRegNumber('');
        loadVehicles();
        return true;
      } else {
        const errorData = response.data as any;
        setError(errorData?.detail || 'Failed to update vehicle');
        return false;
      }
    } catch (e: any) {
      setError(e?.message || 'Error updating vehicle');
      return false;
    }
  };

  const handleUnassignVehicle = (vehicle: Vehicle) => {
    if (Platform.OS === 'web') {
      if (window.confirm(t('myVehicles.confirmUnassign', { plate: vehicle.registration_number }))) {
        performUnassign(vehicle.id);
      }
    } else {
      Alert.alert(
        t('myVehicles.unassignTitle'),
        t('myVehicles.confirmUnassign', { plate: vehicle.registration_number }),
        [
          { text: t('common.cancel'), style: 'cancel' },
          { text: t('common.remove'), onPress: () => performUnassign(vehicle.id), style: 'destructive' },
        ]
      );
    }
  };

  const performUnassign = async (vehicleId: number) => {
    try {
      const response = await unassignMyVehicle(vehicleId);
      if (response.ok) {
        loadVehicles();
      } else {
        const errorData = response.data as any;
        setError(errorData?.detail || 'Failed to remove vehicle');
      }
    } catch (e: any) {
      setError(e?.message || 'Error removing vehicle');
    }
  };

  const openAddModal = () => {
    setEditingVehicle(null);
    form.reset({
      registration_number: '',
      fuel_type: 'gasolina',
      make: '',
      model: '',
      year: new Date().getFullYear(),
    });
    setLocalRegNumber('');
    setModalVisible(true);
  };

  const openEditModal = (vehicle: Vehicle) => {
    setEditingVehicle(vehicle);
    form.reset({
      registration_number: vehicle.registration_number,
      fuel_type: vehicle.fuel_type || 'gasolina',
      make: vehicle.make || '',
      model: vehicle.model || '',
      year: vehicle.year || new Date().getFullYear(),
    });
    setLocalRegNumber(vehicle.registration_number);
    setModalVisible(true);
  };

  const formatRegistrationNumber = (input: string): string => {
    const cleaned = input.replace(/[^0-9A-Za-z]/g, '').toUpperCase();
    if (cleaned.length <= 2) return cleaned;
    if (cleaned.length <= 4) return `${cleaned.slice(0, 2)}-${cleaned.slice(2)}`;
    if (cleaned.length <= 6) return `${cleaned.slice(0, 2)}-${cleaned.slice(2, 4)}-${cleaned.slice(4)}`;
    return `${cleaned.slice(0, 2)}-${cleaned.slice(2, 4)}-${cleaned.slice(4, 6)}`;
  };

  // Calcular height total do header incluindo status bar
  const headerHeight = 48; // Altura compacta para mobile
  const topPadding = Platform.OS === 'ios' ? insets.top : Platform.OS === 'android' ? insets.top : 0;
  const totalHeaderHeight = isLargeScreen ? 0 : headerHeight + topPadding;

  const renderHeader = () => {
    if (isLargeScreen) return null;
    return (
      <Appbar.Header style={{ 
        height: headerHeight + topPadding, 
        backgroundColor: theme.colors.surface, 
        position: 'absolute', 
        top: 0, 
        left: 0, 
        right: 0, 
        zIndex: 1,
        alignItems: 'center',
        paddingTop: topPadding,
      }}>
        <Appbar.Action icon="menu" onPress={() => setSidebarOpen(true)} color={theme.colors.onSurface} />
        <View style={{ flex: 1, justifyContent: 'center', alignItems: 'center' }}>
          <Logo size={Platform.OS === 'web' ? 'medium' : 'mobile'} />
        </View>
        <View style={{ width: 48 }} />
      </Appbar.Header>
    );
  };

  const renderContent = () => {
    if (loading) {
      return (
        <View style={{ flex: 1, justifyContent: 'center', alignItems: 'center' }}>
          <Text>{t('common.loading')}</Text>
        </View>
      );
    }

    return (
      <ScrollView
        style={{ flex: 1 }}
        contentContainerStyle={{
          padding: 20,
          paddingBottom: 100,
        }}
      >
        {/* First-time tooltip */}
        {!tooltipDismissed && (
          <Surface
            style={{
              padding: 16,
              marginBottom: 20,
              borderRadius: 8,
              backgroundColor: theme.colors.primaryContainer,
            }}
          >
            <View style={{ flexDirection: 'row', justifyContent: 'space-between', alignItems: 'flex-start' }}>
              <View style={{ flex: 1, marginRight: 8 }}>
                <Text variant="titleSmall" style={{ color: theme.colors.onPrimaryContainer, marginBottom: 8 }}>
                  {t('myVehicles.tooltipTitle')}
                </Text>
                <Text variant="bodyMedium" style={{ color: theme.colors.onPrimaryContainer }}>
                  {t('myVehicles.tooltipMessage')}
                </Text>
              </View>
              <IconButton
                icon="close"
                size={20}
                onPress={dismissTooltip}
                iconColor={theme.colors.onPrimaryContainer}
              />
            </View>
          </Surface>
        )}

        {/* Header */}
        <View style={{ flexDirection: 'row', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20 }}>
          <Text variant="headlineMedium">{t('myVehicles.title')}</Text>
          <Button
            mode="contained"
            icon="plus"
            onPress={openAddModal}
          >
            {t('myVehicles.addVehicle')}
          </Button>
        </View>

        {error && (
          <Surface
            style={{
              padding: 16,
              marginBottom: 20,
              borderRadius: 8,
              backgroundColor: theme.colors.errorContainer,
            }}
          >
            <Text style={{ color: theme.colors.onErrorContainer }}>{error}</Text>
          </Surface>
        )}

        {/* Empty state */}
        {vehicles.length === 0 && (
          <Card style={{ padding: 40, alignItems: 'center' }}>
            <Text variant="titleLarge" style={{ marginBottom: 16, textAlign: 'center' }}>
              {t('myVehicles.emptyTitle')}
            </Text>
            <Text variant="bodyMedium" style={{ marginBottom: 24, textAlign: 'center', color: theme.colors.onSurfaceVariant }}>
              {t('myVehicles.emptyMessage')}
            </Text>
            <Button mode="contained" icon="plus" onPress={openAddModal}>
              {t('myVehicles.addFirstVehicle')}
            </Button>
          </Card>
        )}

        {/* Vehicle list */}
        {vehicles.map((vehicle) => (
          <Card key={vehicle.id} style={{ marginBottom: 16 }}>
            <Card.Content>
              <View style={{ flexDirection: 'row', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                <View style={{ flex: 1 }}>
                  <View style={{ flexDirection: 'row', alignItems: 'center', marginBottom: 8 }}>
                    <Chip icon="car" style={{ marginRight: 8 }}>
                      {vehicle.registration_number}
                    </Chip>
                  </View>
                  <Text variant="titleMedium" style={{ marginBottom: 4 }}>
                    {[vehicle.make, vehicle.model].filter(Boolean).join(' ') || t('myVehicles.noDetails')}
                  </Text>
                  <Text variant="bodyMedium" style={{ color: theme.colors.onSurfaceVariant }}>
                    {vehicle.year && `${vehicle.year}`}
                    {vehicle.year && vehicle.fuel_type && ' • '}
                    {vehicle.fuel_type && t(`fuelTypes.${vehicle.fuel_type}`)}
                  </Text>
                </View>
                <View style={{ flexDirection: 'row' }}>
                  <IconButton
                    icon="pencil"
                    onPress={() => openEditModal(vehicle)}
                  />
                  <IconButton
                    icon="delete-outline"
                    onPress={() => handleUnassignVehicle(vehicle)}
                  />
                </View>
              </View>
            </Card.Content>
          </Card>
        ))}
      </ScrollView>
    );
  };

  return (
    <View style={{ flex: 1, flexDirection: 'row', backgroundColor: theme.colors.background }}>
      {isLargeScreen && (
        <Sidebar
          key={`desktop-sidebar-${width}`}
          isOpen={true}
          onClose={() => {}}
          screenWidth={width}
          onLogout={() => router.replace('/auth/login')}
          isOwner={false}
          isDriver={true}
        />
      )}

      <View style={{ flex: 1, marginLeft: isLargeScreen ? SIDEBAR_WIDTH : 0 }}>
        {renderHeader()}
        <View style={{ flex: 1, marginTop: totalHeaderHeight }}>
          {renderContent()}
        </View>
      </View>

      {!isLargeScreen && (
        <Sidebar
          key={`mobile-sidebar-${width}`}
          isOpen={sidebarOpen}
          onClose={() => setSidebarOpen(false)}
          screenWidth={width}
          onLogout={() => router.replace('/auth/login')}
          isOwner={false}
          isDriver={true}
        />
      )}

      {/* Add/Edit Vehicle Modal */}
      <VehicleModal
        visible={modalVisible}
        onDismiss={() => setModalVisible(false)}
        form={form}
        onSubmit={editingVehicle ? handleEditVehicle : handleAddVehicle}
        localRegNumber={localRegNumber}
        setLocalRegNumber={setLocalRegNumber}
        formatRegistrationNumber={formatRegistrationNumber}
        isEditing={!!editingVehicle}
      />
    </View>
  );
}

